import { useMemo } from "react";
import TextField from "@/components/TextField";
import { createFormElement, FormElement } from "@/types/Form";
import AutocompleteField from "@/components/AutocompleteField";
import DateField from "@/components/DateField";
import { queries } from "@/config/theme";
import { Divider } from "antd";
import { css } from "@emotion/react";
import useLookupStore from "@/lookups/stores/lookups";
import { transformRecordsToOptions } from "@/lookups/utils/transform";
import FieldRenderer, {
  FieldWrapper,
  FormSubtitle,
  fullGridStyles,
} from "../ReportForm/utils";

const CommentFieldCss = css`
  grid-column: 1 / -1;
  // half of screen size
  @media (${queries.laptopAndUp}) {
    grid-column: 1 / 5;
  }
`;

const WorkDoneFieldCss = css`
  grid-column: 1 / -1;
  // half of screen size
  @media (${queries.laptopAndUp}) {
    max-width: 50%;
  }
`;

type Props = {
  disabled?: boolean;
};

const useFields = () => {
  const { lookups } = useLookupStore();

  const testFields = useMemo<FormElement[]>(
    () => [
      createFormElement(FormSubtitle, {
        elementContainerCss: fullGridStyles,
        marginTop: "0",
        children: "Final Test Data",
        key: "Final Test Data",
      }),
      createFormElement(TextField, {
        label: "Parts required/Work done",
        name: "tWorkDone",
        type: "textarea",
        rows: 3,
        elementContainerCss: WorkDoneFieldCss,
        width: "50%",
      }),
      createFormElement(TextField, {
        label: "Packing",
        name: "tPacking",
      }),
      createFormElement(TextField, {
        label: "Gasket",
        name: "tGasket",
      }),
      createFormElement(TextField, {
        label: "Torque",
        name: "tTorque",
      }),
      createFormElement(FormSubtitle, {
        marginTop: "0",
        elementContainerCss: fullGridStyles,
        children: "Seat Test Air",
        key: "Seat Test Air",
      }),
      createFormElement(TextField, {
        label: "Side A BPM",
        name: "tSeatTestAirSideABPM",
        type: "number",
        inputMode: "decimal",
      }),
      createFormElement(TextField, {
        label: "Side A Pressure",
        name: "tSeatTestAirSideAPSI",
        type: "number",
        inputMode: "decimal",
        suffix: "PSI",
      }),
      createFormElement(TextField, {
        label: "Side B BPM",
        name: "tSeatTestAirSideBBPM",
        type: "number",
        inputMode: "decimal",
      }),
      createFormElement(TextField, {
        label: "Side B Pressure",
        name: "tSeatTestAirSideBPSI",
        type: "number",
        inputMode: "decimal",
        suffix: "PSI",
      }),
      createFormElement(FormSubtitle, {
        marginTop: "0",
        elementContainerCss: fullGridStyles,
        children: "Seat Test Hydro",
        key: "Seat Test Hydro",
      }),
      createFormElement(TextField, {
        label: "Side A DPM",
        name: "tSeatTestHydroSideADPM",
        type: "number",
        inputMode: "decimal",
      }),
      createFormElement(TextField, {
        label: "Side A Pressure",
        name: "tSeatTestHydroSideAPSI",
        type: "number",
        inputMode: "decimal",
        suffix: "PSI",
      }),
      createFormElement(TextField, {
        label: "Side B DPM",
        name: "tSeatTestHydroSideBDPM",
        type: "number",
        inputMode: "decimal",
      }),
      createFormElement(TextField, {
        label: "Side B Pressure",
        name: "tSeatTestHydroSideBPSI",
        type: "number",
        inputMode: "decimal",
        suffix: "PSI",
      }),
      createFormElement(FormSubtitle, {
        marginTop: "0",
        elementContainerCss: fullGridStyles,
        children: "Other Tests",
        key: "Other Tests",
      }),
      createFormElement(TextField, {
        label: "Shell Test",
        name: "tShellTest",
        type: "number",
        inputMode: "decimal",
      }),
      createFormElement(TextField, {
        label: "Shell Test Pressure",
        name: "tShellTestPSI",
        type: "number",
        inputMode: "decimal",
        suffix: "PSI",
      }),
      createFormElement(TextField, {
        label: "Back Seat Test",
        name: "tBackSeatTest",
        type: "number",
        inputMode: "decimal",
      }),
      createFormElement(TextField, {
        label: "Back Seat Test Pressure",
        name: "tBackSeatTestPSI",
        type: "number",
        inputMode: "decimal",
        suffix: "PSI",
      }),
      createFormElement(FormSubtitle, {
        marginTop: "0",
        elementContainerCss: fullGridStyles,
        children: "Tested to",
        key: "Tested to",
      }),
      createFormElement(AutocompleteField, {
        label: "Test Standard",
        name: "tTestStandard",
        options: transformRecordsToOptions(
          lookups.ManualValveRepairReportTestStandard
        ),
        showSearch: true,
      }),
      createFormElement(AutocompleteField, {
        label: "Assembled By",
        name: "tAssembledBy",
        options: transformRecordsToOptions(lookups.Staff),
        showSearch: true,
      }),
      createFormElement(AutocompleteField, {
        label: "Tested By",
        name: "tTestedBy",
        options: transformRecordsToOptions(lookups.Staff),
        showSearch: true,
      }),
      createFormElement(AutocompleteField, {
        label: "Witnessed By",
        name: "tWitnessedBy",
        options: transformRecordsToOptions(lookups.Staff),
        showSearch: true,
      }),
    ],
    [lookups]
  );
  const extraFields = useMemo(
    () => [
      createFormElement(DateField, {
        label: "Date",
        name: "tDate",
      }),
      createFormElement(TextField, {
        label: "Comments",
        name: "tOtherComments",
        type: "textarea",
        rows: 6,
        elementContainerCss: CommentFieldCss,
      }),
    ],
    []
  );

  return useMemo(
    () => ({ extraFields, testFields }),
    [extraFields, testFields]
  );
};

const ManualFinalTest = ({ disabled }: Props) => {
  const { testFields, extraFields } = useFields();
  return (
    <>
      <FieldWrapper>
        <FieldRenderer fields={testFields} disabled={disabled} />
      </FieldWrapper>
      <Divider />
      <FieldWrapper>
        <FieldRenderer fields={extraFields} disabled={disabled} />
      </FieldWrapper>
    </>
  );
};

export default ManualFinalTest;
